'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { cardReset } from '../_shared/styles';
import type { PaddingBox } from '../_shared/resolvePadding';
import { ContentRail } from '../ui/ContentRail';

type Localized = string | Record<string, string>;

export interface ComingSoonItem {
  title?: Localized;
  description?: Localized;
  image?: string;
  date?: string;
}

/**
 * How the cards lay out.
 * - {@code 'grid'} (default) — the original responsive 4/2/1-column grid.
 * - {@code 'drag'} — a horizontal free-scroll rail; grab and pull with
 *   the mouse.
 * - {@code 'buttons'} — a horizontal rail with free scrolling hidden,
 *   advanced only by the always-visible prev/next arrow pills (paginated
 *   stepper), like a learning journey.
 * - {@code 'scrollbar'} — a horizontal rail scrolled with the mouse wheel
 *   / trackpad, keeping a visible scrollbar along the bottom.
 * - {@code 'wheel'} — the same wheel-driven horizontal scroll as
 *   {@code 'scrollbar'} but with the scrollbar hidden: hover and spin the
 *   wheel down to move right.
 */
export type ComingSoonScrollMode = 'grid' | 'drag' | 'buttons' | 'scrollbar' | 'wheel';

export interface ComingSoonProps {
  title?: Localized;
  description?: Localized;
  limit?: number;
  items?: ComingSoonItem[];
  /** Card layout / scroll behaviour — see {@link ComingSoonScrollMode}.
   *  Defaults to {@code 'grid'}. */
  scrollMode?: ComingSoonScrollMode;
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

/** Demo cards shown when the author has not supplied any of their own.
 *  Built per render so a language switch re-reads the catalogue. */
const demoItems = (t: (key: string) => string): ComingSoonItem[] => [
  {
    title: t('comingSoon.demo.react.title'),
    description: t('comingSoon.demo.react.description'),
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=500',
    date: t('comingSoon.demo.react.date'),
  },
  {
    title: t('comingSoon.demo.cloud.title'),
    description: t('comingSoon.demo.cloud.description'),
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=500',
    date: t('comingSoon.demo.cloud.date'),
  },
  {
    title: t('comingSoon.demo.ml.title'),
    description: t('comingSoon.demo.ml.description'),
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=500',
    date: t('comingSoon.demo.ml.date'),
  },
  {
    title: t('comingSoon.demo.security.title'),
    description: t('comingSoon.demo.security.description'),
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=500',
    date: t('comingSoon.demo.security.date'),
  },
];

export function ComingSoon({
  title,
  description,
  limit = 4,
  items,
  scrollMode = 'grid',
  style,
  paddingOuter,
  headingLine,
  paddingInner,
}: ComingSoonProps) {
  const { t } = useTranslation('site-builder-blocks');
  const raw = asList<ComingSoonItem>(items);
  const list = (raw.length > 0 ? raw : demoItems(t)).slice(0, limit);

  const cards = list.map((item, i) => <ComingSoonCard key={i} item={item} />);

  // In paginated ('buttons') mode the prev/next arrows sit in the rail's
  // own header row (title inline-start, arrows inline-end) — matching
  // LearningJourneys / TestimonialsBento. Suppress SectionShell's header
  // there so the title isn't rendered twice.
  const headerArrows = scrollMode === 'buttons';

  return (
    <SectionShell
      headingLine={headingLine}
      style={style}
      title={headerArrows ? undefined : tx(title)}
      subtitle={headerArrows ? undefined : tx(description)}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      {scrollMode === 'grid' ? (
        <>
          <style>{`
            .coming-soon-grid {
              display: grid;
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: var(--space-4, 16px);
            }
            @media (max-width: 1024px) {
              .coming-soon-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            }
            @media (max-width: 560px) {
              .coming-soon-grid { grid-template-columns: 1fr; }
            }
          `}</style>
          <div className="coming-soon-grid">{cards}</div>
        </>
      ) : (
        <ContentRail
          ariaLabel={tx(title) || t('comingSoon.ariaLabel')}
          itemWidth="clamp(240px, 28vw, 300px)"
          gap="var(--space-4, 16px)"
          scrollMode={scrollMode}
          heading={headerArrows ? tx(title) : undefined}
          subheading={headerArrows ? tx(description) : undefined}
        >
          {cards}
        </ContentRail>
      )}
    </SectionShell>
  );
}

/** A single upcoming-course card. Shared by the grid and rail layouts —
 *  fills the width of whatever cell / rail item wraps it. */
function ComingSoonCard({ item }: { item: ComingSoonItem }): ReactNode {
  return (
    <Card
      style={{
        ...cardReset,
        background: 'var(--color-surface-card, #fff)',
        borderRadius: 'var(--radius-md, 12px)',
        overflow: 'hidden',
      }}
    >
      {item.image && (
        <div style={{ position: 'relative', aspectRatio: '16/9', overflow: 'hidden' }}>
          <img
            src={item.image}
            alt={tx(item.title) ?? ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {item.date && (
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                background: 'rgba(0,0,0,0.55)',
                color: 'var(--color-text, #fff)',
                fontSize: '11px',
                fontWeight: 600,
                padding: 'var(--space-1, 4px) var(--space-2, 8px)',
                borderRadius: 'var(--radius-sm, 6px)',
                backdropFilter: 'blur(4px)',
              }}
            >
              {item.date}
            </div>
          )}
        </div>
      )}
      <div style={{ padding: 'var(--space-4, 16px)' }}>
        {item.title && (
          <div
            style={{
              fontSize: 'var(--font-size-base, 15px)',
              fontWeight: 600,
              color: 'var(--color-text-primary, #18181b)',
              marginBottom: 6,
            }}
          >
            {tx(item.title)}
          </div>
        )}
        {item.description && (
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {tx(item.description)}
          </p>
        )}
      </div>
    </Card>
  );
}
